import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Plus, Pencil, Trash2, FileStack, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGIME_LABELS = { contract: "Contrato", green_receipts: "Recibos Verdes", b2b: "B2B" };
const BIZ_LABELS = { outsourcing: "Outsourcing", perm: "Perm" };

const emptyForm = {
  name: "", description: "", business_type: "outsourcing", regime_type: "contract",
  gross_value: "", client_rate: "", client_rate_type: "hourly", billable_months: "11",
  daily_rate: "", perm_candidate_salary: "", perm_percentage_fee: "15",
  meal_allowance_daily: "9.60", health_insurance_cost: "0",
  insurance_capitalization: "0", ticket_infancia: "0", coverflex: "0", km_allowance: "0",
};

const AdminTemplates = () => {
  const { getAuthHeaders } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/templates`, { headers: getAuthHeaders() });
      setTemplates(res.data);
    } catch { toast.error("Erro ao carregar templates"); }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const openNew = () => {
    setEditingTemplate(null);
    setForm(emptyForm);
    setEditDialog(true);
  };

  const openEdit = (t) => {
    setEditingTemplate(t);
    const fd = t.form_data || {};
    setForm({
      name: t.name || "", description: t.description || "",
      business_type: t.business_type || "outsourcing", regime_type: t.regime_type || "contract",
      gross_value: fd.gross_value?.toString() || "", client_rate: fd.client_rate?.toString() || "",
      client_rate_type: fd.client_rate_type || "hourly", billable_months: fd.billable_months?.toString() || "11",
      daily_rate: fd.daily_rate?.toString() || "",
      perm_candidate_salary: fd.perm_candidate_salary?.toString() || "",
      perm_percentage_fee: fd.perm_percentage_fee?.toString() || "15",
      meal_allowance_daily: fd.meal_allowance_daily?.toString() || "9.60",
      health_insurance_cost: fd.health_insurance_cost?.toString() || "0",
      insurance_capitalization: fd.insurance_capitalization?.toString() || "0",
      ticket_infancia: fd.ticket_infancia?.toString() || "0",
      coverflex: fd.coverflex?.toString() || "0",
      km_allowance: fd.km_allowance?.toString() || "0",
    });
    setEditDialog(true);
  };

  const saveTemplate = async () => {
    if (!form.name.trim()) { toast.error("Nome obrigatório"); return; }
    setSaving(true);
    const formData = {};
    const numFields = ["gross_value","client_rate","billable_months","daily_rate","perm_candidate_salary","perm_percentage_fee","meal_allowance_daily","health_insurance_cost","insurance_capitalization","ticket_infancia","coverflex","km_allowance"];
    numFields.forEach(k => { if (form[k]) formData[k] = parseFloat(form[k]); });
    formData.client_rate_type = form.client_rate_type;
    formData.value_type = "monthly";

    try {
      if (editingTemplate) {
        await axios.put(`${API}/templates/${editingTemplate.id}`, {
          name: form.name, description: form.description, form_data: formData,
        }, { headers: getAuthHeaders() });
        toast.success("Template atualizado");
      } else {
        await axios.post(`${API}/templates`, {
          name: form.name, description: form.description,
          business_type: form.business_type, regime_type: form.regime_type,
          form_data: formData,
        }, { headers: getAuthHeaders() });
        toast.success("Template criado");
      }
      setEditDialog(false);
      loadTemplates();
    } catch { toast.error("Erro ao guardar"); }
    finally { setSaving(false); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("Eliminar este template?")) return;
    try {
      await axios.delete(`${API}/templates/${id}`, { headers: getAuthHeaders() });
      setTemplates(prev => prev.filter(t => t.id !== id));
      toast.success("Template eliminado");
    } catch { toast.error("Erro ao eliminar"); }
  };

  const isPerm = form.business_type === "perm";
  const isFreelancer = form.regime_type === "green_receipts" || form.regime_type === "b2b";

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[var(--brand-primary)]" /></div>;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-templates">
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-2" />Voltar</Button></Link>
            <FileStack className="w-5 h-5 text-[var(--brand-primary)]" />
            <span className="font-manrope font-bold text-lg">Templates de Simulação</span>
            <span className="text-xs text-muted-foreground bg-slate-100 px-2 py-0.5 rounded">{templates.length}</span>
          </div>
          <Button onClick={openNew} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary)] text-white" data-testid="new-template-btn">
            <Plus className="w-4 h-4 mr-2" />Novo Template
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {templates.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <FileStack className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Nenhum template criado.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map(t => (
              <Card key={t.id} className="hover:border-[var(--brand-primary)]/30 transition-colors group">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-sm font-semibold">{t.name}</CardTitle>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(t)} data-testid={`edit-template-${t.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => deleteTemplate(t.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  {t.description && <p className="text-xs text-muted-foreground mb-2">{t.description}</p>}
                  <div className="flex gap-2 flex-wrap">
                    <span className="text-[10px] px-2 py-0.5 rounded bg-slate-100 font-medium">{BIZ_LABELS[t.business_type] || t.business_type}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded bg-blue-50 text-blue-700 font-medium">{REGIME_LABELS[t.regime_type] || t.regime_type}</span>
                    {t.form_data?.gross_value && <span className="text-[10px] px-2 py-0.5 rounded bg-brand-50 text-brand-700">{t.form_data.gross_value}€ bruto</span>}
                    {t.form_data?.client_rate && <span className="text-[10px] px-2 py-0.5 rounded bg-violet-50 text-violet-700">{t.form_data.client_rate}€/h rate</span>}
                    {t.form_data?.daily_rate && <span className="text-[10px] px-2 py-0.5 rounded bg-brand-50 text-brand-700">{t.form_data.daily_rate}€/dia</span>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? "Editar Template" : "Novo Template"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Nome *</Label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1" data-testid="template-form-name" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Descrição</Label>
                <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="mt-1" />
              </div>
              {!editingTemplate && (
                <>
                  <div>
                    <Label className="text-xs">Tipo de Negócio</Label>
                    <Select value={form.business_type} onValueChange={v => setForm({...form, business_type: v})}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="outsourcing">Outsourcing</SelectItem>
                        <SelectItem value="perm">Perm</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Regime</Label>
                    <Select value={form.regime_type} onValueChange={v => setForm({...form, regime_type: v})}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="contract">Contrato</SelectItem>
                        <SelectItem value="green_receipts">Recibos Verdes</SelectItem>
                        <SelectItem value="b2b">B2B</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>

            <div className="border-t pt-3">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Valores Padrão</p>
              <div className="grid grid-cols-2 gap-3">
                {!isPerm && !isFreelancer && (
                  <div>
                    <Label className="text-xs">Salário Bruto (€)</Label>
                    <Input type="number" value={form.gross_value} onChange={e => setForm({...form, gross_value: e.target.value})} className="mt-1" />
                  </div>
                )}
                {isFreelancer && (
                  <div>
                    <Label className="text-xs">Valor Dia (€)</Label>
                    <Input type="number" value={form.daily_rate} onChange={e => setForm({...form, daily_rate: e.target.value})} className="mt-1" />
                  </div>
                )}
                {isPerm && (
                  <>
                    <div>
                      <Label className="text-xs">Salário Candidato (€)</Label>
                      <Input type="number" value={form.perm_candidate_salary} onChange={e => setForm({...form, perm_candidate_salary: e.target.value})} className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs">Fee (%)</Label>
                      <Input type="number" value={form.perm_percentage_fee} onChange={e => setForm({...form, perm_percentage_fee: e.target.value})} className="mt-1" />
                    </div>
                  </>
                )}
                <div>
                  <Label className="text-xs">Rate Cliente (€)</Label>
                  <Input type="number" value={form.client_rate} onChange={e => setForm({...form, client_rate: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Meses Faturáveis</Label>
                  <Input type="number" value={form.billable_months} onChange={e => setForm({...form, billable_months: e.target.value})} className="mt-1" />
                </div>
              </div>
            </div>

            {!isPerm && !isFreelancer && (
              <div className="border-t pt-3">
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Benefícios</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Refeição/dia (€)</Label>
                    <Input type="number" value={form.meal_allowance_daily} onChange={e => setForm({...form, meal_allowance_daily: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Seguro Saúde (€)</Label>
                    <Input type="number" value={form.health_insurance_cost} onChange={e => setForm({...form, health_insurance_cost: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Seguro Real Vida (€)</Label>
                    <Input type="number" value={form.insurance_capitalization} onChange={e => setForm({...form, insurance_capitalization: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">T. Infância (€)</Label>
                    <Input type="number" value={form.ticket_infancia} onChange={e => setForm({...form, ticket_infancia: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Coverflex (€)</Label>
                    <Input type="number" value={form.coverflex} onChange={e => setForm({...form, coverflex: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Deslocação (€)</Label>
                    <Input type="number" value={form.km_allowance} onChange={e => setForm({...form, km_allowance: e.target.value})} className="mt-1" />
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>Cancelar</Button>
            <Button onClick={saveTemplate} disabled={saving} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary)] text-white" data-testid="save-template-form-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingTemplate ? "Atualizar" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTemplates;
